import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { base64ADatos } from "./assessmentExtractor";
import { obtenerDimensionesImagen, ajustarProporcional } from "./imageDimensions";

const RUTA_MACHOTE = `${process.env.PUBLIC_URL || ""}/documentos/Machote_propuesta_comercial.docx`;

const RUTAS_LOGO_EMPRESA = {
  Netjer: `${process.env.PUBLIC_URL || ""}/img/NJ-Logotipo.png`,
  Rexten: `${process.env.PUBLIC_URL || ""}/img/rexten.png`,
};

const NOMBRES_EMPRESA = {
  Netjer: "Netjer Networks",
  Rexten: "Rexten Global Communications",
};

const NOMBRES_EMPRESA_LEGAL = {
  Netjer: "Netjer Networks México S.A. de C.V.",
  Rexten: "Rexten Global Communications S.A. de C.V.",
};

const PIXEL_TRANSPARENTE_BASE64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";

export const MONEDAS = {
  USD: {
    nombre: "Dólares Americanos",
    codigo: "USD",
    textoPago: "Dólares Americanos (USD)",
  },
  MXN: {
    nombre: "Pesos Mexicanos",
    codigo: "MXN",
    textoPago: "Moneda Nacional (pesos mexicanos)",
  },
};

const MONEDA_DEFAULT = "USD";

// Datos bancarios por empresa y moneda: cada empresa maneja una cuenta
// distinta según se cotice en USD o en MXN. Rexten/MXN tiene datos reales;
// el resto son de prueba mientras se obtienen las cuentas definitivas.
const DATOS_PAGO_POR_EMPRESA_MONEDA = {
  Rexten: {
    MXN: {
      banco: "BBVA Bancomer",
      numeroCuenta: "0159626794",
      clabe: "012180001596267947",
    },
    USD: {
      banco: "Banco de prueba Rexten USD",
      numeroCuenta: "1111111111",
      clabe: "011111111111111111",
    },
  },
  Netjer: {
    MXN: {
      banco: "Banco de prueba Netjer MXN",
      numeroCuenta: "2222222222",
      clabe: "022222222222222222",
    },
    USD: {
      banco: "Banco de prueba Netjer USD",
      numeroCuenta: "3333333333",
      clabe: "033333333333333333",
    },
  },
};

const DATOS_PAGO_DEFAULT = DATOS_PAGO_POR_EMPRESA_MONEDA.Rexten.MXN;

const cargarLogoEmpresa = async (empresa) => {
  const ruta = RUTAS_LOGO_EMPRESA[empresa] || RUTAS_LOGO_EMPRESA.Netjer;
  const respuesta = await fetch(ruta);
  if (!respuesta.ok) {
    throw new Error(`No se pudo cargar el logo de la empresa (${ruta}).`);
  }
  return new Uint8Array(await respuesta.arrayBuffer());
};

const TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA = [220, 100];
const TAMANO_MAXIMO_LOGO_EMPRESA_PORTADA = [286, 143];
const TAMANO_MAXIMO_LOGO_EMPRESA_ENCABEZADO = [103, 52];
const TAMANO_MAXIMO_LOGO_CLIENTE_ENCABEZADO = [150, 60];

const TAMANOS_MAXIMOS_POR_TAG = {
  logoCliente: TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA,
  logoClienteEncabezado: TAMANO_MAXIMO_LOGO_CLIENTE_ENCABEZADO,
  logoEmpresaPortada: TAMANO_MAXIMO_LOGO_EMPRESA_PORTADA,
  logoEmpresaEncabezado: TAMANO_MAXIMO_LOGO_EMPRESA_ENCABEZADO,
};

const crearModuloImagenes = () =>
  new ImageModule({
    centered: false,
    fileType: "docx",
    getImage: (tagValue) => tagValue,
    getSize: (imgBuffer, _tagValue, tagName) => {
      const tamanoMaximo = TAMANOS_MAXIMOS_POR_TAG[tagName] || TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA;
      return ajustarProporcional(obtenerDimensionesImagen(imgBuffer), tamanoMaximo);
    },
  });

export const generarDocumentoPropuestaComercial = async ({
  nombreProyecto,
  fechaCreacion,
  nombreCliente,
  contactoCliente,
  puestoContactoCliente,
  logoCliente,
  empresa,
  firmante,
  moneda,
}) => {
  const [respuesta, datosLogoEmpresa] = await Promise.all([
    fetch(RUTA_MACHOTE),
    cargarLogoEmpresa(empresa),
  ]);
  if (!respuesta.ok) {
    throw new Error(
      "No se pudo cargar el machote de Propuesta Comercial (public/documentos/Machote_propuesta_comercial.docx)."
    );
  }
  const arrayBuffer = await respuesta.arrayBuffer();
  const zip = new PizZip(arrayBuffer);

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    nullGetter: () => "",
    modules: [crearModuloImagenes()],
  });

  const datosLogoCliente = logoCliente
    ? base64ADatos(logoCliente.base64)
    : base64ADatos(PIXEL_TRANSPARENTE_BASE64);

  const datosMoneda = MONEDAS[moneda] || MONEDAS[MONEDA_DEFAULT];
  const datosPago = (DATOS_PAGO_POR_EMPRESA_MONEDA[empresa] && DATOS_PAGO_POR_EMPRESA_MONEDA[empresa][datosMoneda.codigo]) || DATOS_PAGO_DEFAULT;

  try {
    await doc.renderAsync({
      nombreProyecto,
      fechaCreacion,
      nombreCliente,
      contactoCliente: contactoCliente || "",
      puestoContactoCliente: puestoContactoCliente || "",
      nombreEmpresa: NOMBRES_EMPRESA[empresa] || empresa,
      nombreEmpresaLegal: NOMBRES_EMPRESA_LEGAL[empresa] || empresa,
      logoCliente: datosLogoCliente,
      logoClienteEncabezado: datosLogoCliente,
      logoEmpresaPortada: datosLogoEmpresa,
      logoEmpresaEncabezado: datosLogoEmpresa,
      nombreFirmante: firmante.nombre,
      puestoFirmante: firmante.puesto,
      correoFirmante: firmante.correo,
      celularFirmante: firmante.celular,
      monedaNombre: datosMoneda.nombre,
      monedaCodigo: datosMoneda.codigo,
      monedaTextoPago: datosMoneda.textoPago,
      banco: datosPago.banco,
      numeroCuenta: datosPago.numeroCuenta,
      clabe: datosPago.clabe,
    });
  } catch (error) {
    const detalle = error.properties && error.properties.errors
      ? error.properties.errors.map((e) => e.message).join("; ")
      : error.message;
    throw new Error(`No se pudo generar la Propuesta Comercial a partir del machote: ${detalle}`);
  }

  return doc.toBlob();
};